/**
 * Scaffold-local events API wrapper.
 *
 * Calls apiRaw() — the typed fetch choke point from @v0y4ge/api-client —
 * and adds ?scenario= to the request URL so the MSW handler can return
 * the correct scenario fixture.
 *
 * In the REAL Studio app, use events.listStudioEvents() and
 * events.createStudioEvent() from @v0y4ge/api-client directly.
 */

import { apiRaw } from "@v0y4ge/api-client";
import type {
  StudioEventsListResponse,
  StudioEventDto,
  StudioEventCreateInput,
} from "@v0y4ge/api-contracts";

export interface EventsListParams {
  page?: number;
  limit?: number;
  // 'past' is accepted by the contract's parseStudioEventsListParams (expands to
  // completed + archived server-side); widened here so the Ended tab is faithful.
  status?: "live" | "scheduled" | "completed" | "cancelled" | "archived" | "past";
  from?: string;
  to?: string;
  location?: string;
  /** Canonical location id filter — StudioEventsListQuery's `locationId`
   * (the worker also reads `location_id`). Distinct from `location`, which
   * filters by location NAME. */
  locationId?: string;
  q?: string;
  sort?: "upcoming" | "created_desc" | "start_asc" | "start_desc" | "title" | "status" | "start" | "location" | "created";
  dir?: "asc" | "desc";
  view?: "archive";
}

/**
 * Fetch the Studio events list through the api-client's typed choke point.
 * Adds `scenario` as a passthrough query param for MSW scenario switching.
 * The real events worker silently ignores unrecognised query params.
 */
export async function fetchEventsList(
  params: EventsListParams = {},
  scenario = "default",
): Promise<StudioEventsListResponse> {
  const qs = new URLSearchParams();

  if (params.page != null) qs.set("page", String(params.page));
  if (params.limit != null) qs.set("limit", String(params.limit));
  if (params.status) qs.set("status", params.status);
  if (params.from) qs.set("from", params.from);
  if (params.to) qs.set("to", params.to);
  if (params.location) qs.set("location", params.location);
  if (params.locationId) qs.set("locationId", params.locationId);
  if (params.q) qs.set("q", params.q);
  if (params.sort) qs.set("sort", params.sort);
  if (params.dir) qs.set("dir", params.dir);
  if (params.view) qs.set("view", params.view);

  if (scenario && scenario !== "default") qs.set("scenario", scenario);

  const path = `/v1/admin/events${qs.size > 0 ? `?${qs}` : ""}`;
  return apiRaw<StudioEventsListResponse>(path, undefined, 'fetchEventsList');
}

/**
 * Create a new event through the api-client typed choke point.
 * MSW intercepts POST /v1/admin/events and mutates the stateful store —
 * a subsequent fetchEventsList() will include the new event.
 *
 * In the REAL Studio app, call events.createStudioEvent() from
 * @v0y4ge/api-client with the admin baseUrl from studio-shell.
 */
export async function createEvent(
  input: StudioEventCreateInput,
): Promise<StudioEventDto> {
  return apiRaw<StudioEventDto>("/v1/admin/events", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  }, 'createEvent');
}

/**
 * Upload raw bytes to a pre-signed storage URL (docket 2026-07-27 item 2).
 * Not a typed-client JSON op — a signed-URL PUT target is frequently a
 * different origin/provider than our own API, so it can't route through
 * apiRaw() — but a raw `fetch` still shouldn't sit inline in a screen
 * component (it's exactly what the intake gate's raw-fetch scan flags, even
 * when, as here, it's the standard justified pattern for a pre-signed
 * upload). Screens call this instead of `fetch` directly.
 */
export async function uploadToSignedUrl(url: string, file: File | Blob): Promise<void> {
  // raw-fetch-ok: PUT to a presigned object-storage URL, different origin than our API -- v0y4ge/voyage-fleet-management#972
  const res = await fetch(url, { method: "PUT", body: file });
  if (!res.ok) throw new Error(`upload target rejected the bytes (${res.status})`);
}
